import { useState } from 'react';

export default function HeroSection() {
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    adults: '1',
    children: '0',
    cabin: 'Economy',
    name: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.from || !formData.to || !formData.departureDate || !formData.name || !formData.phone) {
      setSubmitMessage('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('from', formData.from);
      formDataToSend.append('to', formData.to);
      formDataToSend.append('departureDate', formData.departureDate);
      formDataToSend.append('returnDate', formData.returnDate || 'One Way');
      formDataToSend.append('passengers', `${formData.adults} Adults, ${formData.children} Children`);
      formDataToSend.append('cabin', formData.cabin);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d6b2rinmvg9ih2c7av70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        // Generate WhatsApp message
        const whatsappMessage = `Hello Alwaz Travels, I would like a flight quote:%0A%0AFrom: ${formData.from}%0ATo: ${formData.to}%0ADeparture Date: ${formData.departureDate}%0AReturn Date: ${formData.returnDate || 'One Way'}%0APassengers: ${formData.adults} Adults, ${formData.children} Children%0ACabin: ${formData.cabin}%0AName: ${formData.name}%0APhone: ${formData.phone}`;
        
        // Open WhatsApp
        window.open(`https://wa.me/447362505895?text=${whatsappMessage}`, '_blank');
        
        setSubmitMessage('Redirecting to WhatsApp...');
        setFormData({
          from: '',
          to: '',
          departureDate: '',
          returnDate: '',
          adults: '1',
          children: '0',
          cabin: 'Economy',
          name: '',
          phone: '',
        });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/bf643f4d230fb490502acf6e68a0208b.jpg"
          alt="Aviation Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Discounted Worldwide Air Tickets<br />Save More When You Book With Us
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Negotiated fares • Family deals • Umrah groups • Last-minute offers • Worldwide destinations
          </p>
        </div>

        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Get Your Best Fare Quote
            </h2>
            <p className="text-gray-600 text-base">Fill in your travel details and we'll send you the best available fares on WhatsApp</p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <input type="hidden" name="form-id" value="flight-quote-request" />
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  From (Departure City) *
                </label>
                <div className="relative">
                  <i className="ri-flight-takeoff-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="text"
                    name="from"
                    value={formData.from}
                    onChange={handleChange}
                    placeholder="e.g., London Heathrow"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  To (Destination) *
                </label>
                <div className="relative">
                  <i className="ri-flight-land-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="text"
                    name="to"
                    value={formData.to}
                    onChange={handleChange}
                    placeholder="e.g., Karachi"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Departure Date *
                </label>
                <div className="relative">
                  <i className="ri-calendar-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="date"
                    name="departureDate"
                    value={formData.departureDate}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Return Date (Optional)
                </label>
                <div className="relative">
                  <i className="ri-calendar-check-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="date"
                    name="returnDate"
                    value={formData.returnDate}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Adults
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <select
                    name="adults"
                    value={formData.adults}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors cursor-pointer"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Children
                </label>
                <div className="relative">
                  <i className="ri-user-smile-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <select
                    name="children"
                    value={formData.children}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors cursor-pointer"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cabin Class
                </label>
                <div className="relative">
                  <i className="ri-vip-crown-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <select
                    name="cabin"
                    value={formData.cabin}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors cursor-pointer"
                  >
                    <option value="Economy">Economy</option>
                    <option value="Premium Economy">Premium Economy</option>
                    <option value="Business">Business</option>
                    <option value="First Class">First Class</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <div className="relative">
                  <i className="ri-user-3-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-[#14B8A6] text-xl"></i>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7XXX XXXXXX"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#14B8A6] focus:outline-none text-sm transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#14B8A6] text-white py-4 rounded-lg font-semibold text-lg hover:bg-[#0F9688] transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              {isSubmitting ? 'Processing...' : 'Get Best Fare on WhatsApp'}
            </button>

            {submitMessage && (
              <p className={`text-center text-sm ${submitMessage.includes('Redirecting') ? 'text-green-600' : 'text-red-600'}`}>
                {submitMessage}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
